import { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Owner-only edit modal for a roast profile. Hits
 *   PATCH /api/roast-profiles/{id}
 * which auto-reconciles inventory when green_weight_kg or lot_id
 * changes. Renders a live "inventory impact" preview so the admin
 * can see the side effects BEFORE saving.
 *
 * Fields the admin can edit (matches the backend contract):
 *   green_weight_kg, roasted_weight_kg, origin_id, lot_id,
 *   start_datetime, end_datetime, machine_id, operator_id, note, flags
 */
export default function RoastEditModal({ roastId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [orig, setOrig] = useState(null);    // original DB doc — inventory diff reference
  const [form, setForm] = useState(null);
  const [origins, setOrigins] = useState([]);
  const [lots, setLots] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);

  // ── Load all master data + the roast in parallel ──
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/roast-profiles/${roastId}`),
      api.get("/origins"),
      api.get("/machines"),
      api.get("/operators"),
    ]).then(([r, o, m, op]) => {
      if (!alive) return;
      const rp = r.data;
      setOrig(rp);
      setForm({
        green_weight_kg: rp.green_weight_kg ?? "",
        roasted_weight_kg: rp.roasted_weight_kg ?? "",
        origin_id: rp.origin_id ?? "",
        lot_id: rp.lot_id ?? "",
        start_datetime: toLocalIso(rp.start_datetime),
        end_datetime: toLocalIso(rp.end_datetime),
        machine_id: rp.machine_id ?? "",
        operator_id: rp.operator_id ?? "",
        note: rp.note ?? "",
        flags: (rp.flags || []).join(", "),
      });
      setOrigins(o.data || []);
      setMachines(m.data || []);
      setOperators(op.data || []);
    }).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Failed to load"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [roastId]);

  // ── Load lots for the currently-selected origin ──
  useEffect(() => {
    if (!form?.origin_id) { setLots([]); return; }
    api.get(`/origins/${form.origin_id}/lots`)
      .then((r) => setLots(r.data || []))
      .catch(() => setLots([]));
  }, [form?.origin_id]);

  const inventoryDelta = useMemo(() => {
    if (!orig || !form) return null;
    const oldGreen = num(orig.green_weight_kg);
    const newGreen = num(form.green_weight_kg);
    const oldLot = orig.lot_id || null;
    const newLot = form.lot_id || null;
    if (oldGreen === newGreen && oldLot === newLot) return null;
    // Suppress the "inventory will be adjusted" banner for roasts that
    // neither had nor gained a lot linkage — nothing to reconcile even
    // though the weights changed.
    if (!oldLot && !newLot) return null;
    return { oldLot, newLot, oldGreen, newGreen };
  }, [orig, form]);

  const lossPreview = useMemo(() => {
    if (!form) return null;
    const g = num(form.green_weight_kg);
    const r = num(form.roasted_weight_kg);
    if (g > 0 && r >= 0) return ((g - r) / g * 100).toFixed(1);
    return null;
  }, [form?.green_weight_kg, form?.roasted_weight_kg]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Build a payload of ONLY the changed fields — cleaner audit trail
    // and avoids re-writing every value on every save.
    const payload = {};
    const maybe = (key, transform = (v) => v) => {
      const before = orig[key] ?? null;
      const after = transform(form[key]);
      const noBoth = (before === null || before === undefined || before === "")
                     && (after === null || after === undefined || after === "");
      if (noBoth) return;
      if (before !== after) payload[key] = after;
    };
    maybe("green_weight_kg", (v) => v === "" ? null : parseFloat(v));
    maybe("roasted_weight_kg", (v) => v === "" ? null : parseFloat(v));
    maybe("origin_id", (v) => v || null);
    maybe("lot_id", (v) => v || null);
    maybe("machine_id", (v) => v || null);
    maybe("operator_id", (v) => v || null);
    maybe("note", (v) => v || null);
    maybe("start_datetime", (v) => fromLocalIso(v));
    maybe("end_datetime", (v) => fromLocalIso(v));
    // Flags: compare comma-separated strings
    const oldFlags = (orig.flags || []).join(", ");
    const newFlags = (form.flags || "").split(",").map((f) => f.trim()).filter(Boolean);
    if (oldFlags !== newFlags.join(", ")) payload.flags = newFlags;

    if (Object.keys(payload).length === 0) {
      setError("No changes to save.");
      return;
    }

    if (inventoryDelta) {
      const msg = `This will adjust green stock:\n\n${describeInventoryDelta(inventoryDelta, origins, lots, orig)}\n\nContinue?`;
      if (!window.confirm(msg)) return;
    }

    setSaving(true);
    try {
      await api.patch(`/roast-profiles/${roastId}`, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="roast-edit-modal">
      <div className="bg-card border border-border rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <p className="label-tiny">Admin edit</p>
            <h3 className="font-display text-lg font-bold">
              Roast {orig ? `#${orig.roast_number || orig.source_uid || "—"}` : "…"}
            </h3>
          </div>
          <button onClick={onClose} data-testid="roast-edit-close" className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}

        {!loading && form && (
          <form onSubmit={onSubmit} className="p-6 space-y-4" data-testid="roast-edit-form">
            <div className="grid grid-cols-2 gap-4">
              <F label="Green weight (kg)">
                <input type="number" step="0.001" min="0" required
                  value={form.green_weight_kg} onChange={upd(setForm, "green_weight_kg")}
                  className={INP} data-testid="edit-green" />
              </F>
              <F label="Roasted weight (kg)">
                <input type="number" step="0.001" min="0"
                  value={form.roasted_weight_kg} onChange={upd(setForm, "roasted_weight_kg")}
                  className={INP} data-testid="edit-roasted" />
              </F>
            </div>
            {lossPreview !== null && (
              <p className="text-xs text-muted-foreground -mt-2">
                Loss will be <strong className="text-foreground">{lossPreview}%</strong>
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <F label="Origin">
                <select value={form.origin_id} onChange={upd(setForm, "origin_id", () => setForm((f) => ({ ...f, lot_id: "" })))}
                  className={INP} data-testid="edit-origin">
                  <option value="">— none —</option>
                  {origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </F>
              <F label="Lot" hint={lots.length === 0 && form.origin_id ? "No lots for this origin" : null}>
                <select value={form.lot_id} onChange={upd(setForm, "lot_id")}
                  className={INP} data-testid="edit-lot">
                  <option value="">— unlinked —</option>
                  {lots.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.lot_ref || l.id.slice(0, 6)} · {num(l.remaining_kg).toFixed(1)} kg remaining
                    </option>
                  ))}
                </select>
              </F>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <F label="Start"><input type="datetime-local" value={form.start_datetime}
                onChange={upd(setForm, "start_datetime")} className={INP} data-testid="edit-start" /></F>
              <F label="End"><input type="datetime-local" value={form.end_datetime}
                onChange={upd(setForm, "end_datetime")} className={INP} data-testid="edit-end" /></F>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <F label="Machine">
                <select value={form.machine_id} onChange={upd(setForm, "machine_id")}
                  className={INP} data-testid="edit-machine">
                  <option value="">— none —</option>
                  {machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </F>
              <F label="Operator">
                <select value={form.operator_id} onChange={upd(setForm, "operator_id")}
                  className={INP} data-testid="edit-operator">
                  <option value="">— none —</option>
                  {operators.map((op) => <option key={op.id} value={op.id}>{op.name || op.email}</option>)}
                </select>
              </F>
            </div>

            <F label="Note">
              <textarea rows={2} value={form.note} onChange={upd(setForm, "note")} className={INP} data-testid="edit-note" />
            </F>

            <F label="Flags" hint="Comma-separated. E.g. 'scorch, tipping'">
              <input type="text" value={form.flags} onChange={upd(setForm, "flags")} className={INP} data-testid="edit-flags" />
            </F>

            {inventoryDelta && (
              <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900" data-testid="inventory-delta">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-orange-600" />
                <div>
                  <p className="font-medium mb-1">Inventory will be adjusted</p>
                  <p className="whitespace-pre-line">
                    {describeInventoryDelta(inventoryDelta, origins, lots, orig)}
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="edit-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
              <button type="submit" disabled={saving} data-testid="edit-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
                <CheckCircle2 className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const INP = "w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";
const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v));

function F({ label, hint, children }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function upd(setForm, key, after) {
  return (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    after?.();
  };
}

// datetime-local input needs "YYYY-MM-DDTHH:MM"; MongoDB ISO strings
// include seconds + timezone. Convert both directions safely.
function toLocalIso(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function fromLocalIso(local) {
  if (!local) return null;
  try {
    const d = new Date(local);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch { return null; }
}

function describeInventoryDelta(d, origins, lots, orig) {
  // Try to find the human-friendly labels for the old / new lot.
  const findLot = (lotId) => {
    if (!lotId) return null;
    const inCur = lots.find((l) => l.id === lotId);
    if (inCur) return inCur;
    // Fall back to a bare id if the old lot isn't in the current origin's list
    return { id: lotId, lot_ref: lotId.slice(0, 6) };
  };
  const oldLot = findLot(d.oldLot);
  const newLot = findLot(d.newLot);
  const lines = [];
  if (d.oldLot && d.oldGreen > 0) {
    lines.push(`+${d.oldGreen.toFixed(3)} kg → lot ${oldLot?.lot_ref || d.oldLot} (credit)`);
  }
  if (d.newLot && d.newGreen > 0) {
    lines.push(`−${d.newGreen.toFixed(3)} kg ← lot ${newLot?.lot_ref || d.newLot} (debit)`);
  }
  // origins arg reserved for future "show origin next to lot" labelling
  void origins; void orig;
  return lines.join("\n") || "No net stock change.";
}
